import { Form, Head } from '@inertiajs/react';
import InputError from '@/theme2/components/input-error';
import PasswordInput from '@/theme2/components/password-input';
import { Button } from '@/theme2/components/ui/button';
import { Input } from '@/theme2/components/ui/input';
import { Label } from '@/theme2/components/ui/label';
import { Spinner } from '@/theme2/components/ui/spinner';

type Props = {
    token: string;
    email: string;
    passwordRules: string;
    formAction?: string;
};

export default function ResetPassword({ token, email, passwordRules, formAction = '/reset-password' }: Props) {
    return (
        <>
            <Head title="Reset password" />

            <div className="mb-8">
                <p className="text-[11px] font-black uppercase tracking-[0.25em] text-neutral-400">Secure your account</p>
                <h2 className="mt-3 text-4xl font-black uppercase tracking-[-0.04em]">New password</h2>
                <p className="mt-3 text-sm leading-6 text-neutral-500">Choose a strong password you haven’t used before.</p>
                <div className="mt-5 grid grid-cols-4 gap-2" aria-label="Password strength guide">
                    <span className="h-1.5 rounded-full bg-black" /><span className="h-1.5 rounded-full bg-neutral-200" /><span className="h-1.5 rounded-full bg-neutral-200" /><span className="h-1.5 rounded-full bg-neutral-200" />
                </div>
                <p className="mt-2 text-xs text-neutral-400">Use 8+ characters with a mix of letters, numbers, and symbols.</p>
            </div>

            <Form
                action={formAction}
                method="post"
                transform={(data) => ({ ...data, token, email })}
                resetOnSuccess={['password', 'password_confirmation']}
            >
                {({ processing, errors }) => (
                    <div className="grid gap-6">
                        <div className="grid gap-2">
                            <Label htmlFor="email">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                name="email"
                                autoComplete="email"
                                value={email}
                                className="mt-1 block w-full"
                                readOnly
                            />
                            <InputError
                                message={errors.email}
                                className="mt-2"
                            />
                        </div>

                        <div className="grid gap-2">
                            <Label htmlFor="password">Password</Label>
                            <PasswordInput
                                id="password"
                                name="password"
                                autoComplete="new-password"
                                className="mt-1 block w-full"
                                autoFocus
                                placeholder="Password"
                                passwordrules={passwordRules}
                            />
                            <InputError message={errors.password} />
                        </div>

                        <div className="grid gap-2">
                            <Label htmlFor="password_confirmation">
                                Confirm password
                            </Label>
                            <PasswordInput
                                id="password_confirmation"
                                name="password_confirmation"
                                autoComplete="new-password"
                                className="mt-1 block w-full"
                                placeholder="Confirm password"
                                passwordrules={passwordRules}
                            />
                            <InputError
                                message={errors.password_confirmation}
                                className="mt-2"
                            />
                        </div>

                        <Button
                            type="submit"
                            className="mt-4 w-full"
                            disabled={processing}
                            data-test="reset-password-button"
                        >
                            {processing && <Spinner />}
                            Reset password
                        </Button>
                    </div>
                )}
            </Form>
        </>
    );
}

ResetPassword.layout = {
    title: 'Reset password',
    description: 'Please enter your new password below',
};
